import { Component, OnInit, inject, model } from '@angular/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '../../base/base.component';
import { GridService } from '../../../services/component-services/grid.service';
import { ModalService, NotificationService } from '../../../services';
import { ImportPreview } from '../../../models';

@Component({
  template: '',
})
export abstract class BaseImportPreviewModalComponent<T extends ImportPreview> extends BaseComponent implements OnInit {
  protected readonly gridService = inject(GridService);
  protected readonly modalService = inject(ModalService);
  protected readonly notificationService = inject(NotificationService);

  public readonly previewData = model<T[]>([]);

  protected columnDefs: ColDef[] = [];
  protected rowData: T[] = [];

  protected validCount = 0;
  protected invalidCount = 0;
  protected warningCount = 0;
  protected newCount = 0;
  protected updateCount = 0;

  protected abstract readonly title: string;

  ngOnInit() {
    this.columnDefs = [...this.getCommonColumns(), ...this.getFeatureColumns()];
    this.rowData = this.previewData();
    this.calculateSummary();
  }

  get hasErrors(): boolean {
    return this.invalidCount > 0;
  }

  get hasValidRows(): boolean {
    return this.validCount > 0;
  }

  protected abstract getFeatureColumns(): ColDef[];

  protected abstract doConfirmImport(validRows: T[]): Observable<any>;

  confirmImport() {
    const validRows = this.rowData.filter((row) => row.isValid);
    this.doConfirmImport(validRows)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success($localize`Import completed successfully`);
          this.modalService.close(true);
        },
        error: () => {
          this.notificationService.fail($localize`Import failed`);
        },
      });
  }

  private getCommonColumns(): ColDef[] {
    return [
      this.gridService.number('rowNumber', $localize`Row`, { width: 70 }),
      this.gridService.text('status', $localize`Status`, {
        width: 100,
        valueGetter: (params: any) => {
          if (!params.data.isValid) return 'Error';
          if (params.data.warnings?.length > 0) return 'Warning';
          return params.data.isExisting ? 'Update' : 'New';
        },
        cellClass: (params: any) => {
          if (!params.data.isValid) return 'text-danger fw-bold';
          if (params.data.warnings?.length > 0) return 'text-warning fw-bold';
          if (params.data.isExisting) return 'text-primary';
          return 'text-success';
        },
      }),
      this.gridService.text('errorsSummary', $localize`Errors`, {
        width: 200,
        valueGetter: (params: any) => (params.data.errors || []).join('; '),
        cellClass: 'text-danger',
      }),
      this.gridService.text('warningsSummary', $localize`Warnings`, {
        width: 200,
        valueGetter: (params: any) => (params.data.warnings || []).join('; '),
        cellClass: 'text-warning',
      }),
    ];
  }

  private calculateSummary() {
    this.validCount = this.rowData.filter((row) => row.isValid).length;
    this.invalidCount = this.rowData.filter((row) => !row.isValid).length;
    this.warningCount = this.rowData.filter((row) => row.warnings?.length > 0).length;
    this.newCount = this.rowData.filter((row) => row.isValid && !row.isExisting).length;
    this.updateCount = this.rowData.filter((row) => row.isValid && row.isExisting).length;
  }
}
